export const MULTIPLE_FRAGMENT_QUALIFIER = "multiple" as const;
export type FragmentCountQualifier = typeof MULTIPLE_FRAGMENT_QUALIFIER;

export interface FragmentCountPatch {
  fragmentCount: number | null;
  fragmentCountQualifier: FragmentCountQualifier | null;
}

type SpecimenFragmentState = {
  fragmentCount?: number | null;
  fragmentCountQualifier?: FragmentCountQualifier | null;
};

const POSITIVE_INTEGER = /^\d+$/;
const MAX_FRAGMENT_COUNT = 999;

export const getFragmentCountDisplayValue = (
  count: number | null | undefined,
  qualifier: FragmentCountQualifier | null | undefined
): string => {
  if (count != null) return String(count);
  return qualifier === MULTIPLE_FRAGMENT_QUALIFIER ? "Multiple" : "";
};

export const getFragmentCountLabel = (
  count: number | null | undefined,
  qualifier: FragmentCountQualifier | null | undefined
): string | null => {
  if (count != null) return `${count} fragment${count === 1 ? "" : "s"}`;
  return qualifier === MULTIPLE_FRAGMENT_QUALIFIER
    ? "Multiple fragments"
    : null;
};

export const normalizeFragmentChipValue = (
  value: string,
  currentCount: number | null | undefined,
  currentQualifier: FragmentCountQualifier | null | undefined
): FragmentCountPatch | null => {
  const normalized = value.trim();
  if (!normalized) return { fragmentCount: null, fragmentCountQualifier: null };
  if (normalized.toLowerCase() === MULTIPLE_FRAGMENT_QUALIFIER) {
    if (currentCount != null) return null;
    return {
      fragmentCount: null,
      fragmentCountQualifier: MULTIPLE_FRAGMENT_QUALIFIER,
    };
  }
  if (!POSITIVE_INTEGER.test(normalized)) return null;
  const count = Number(normalized);
  if (!Number.isSafeInteger(count) || count < 1 || count > MAX_FRAGMENT_COUNT) {
    return null;
  }
  if (count === currentCount && currentQualifier == null) return null;
  return { fragmentCount: count, fragmentCountQualifier: null };
};

export const shouldSynchronizeFragmentChip = (
  previousValue: string | null | undefined,
  nextValue: string | null
): boolean => previousValue !== nextValue;

export const getGrossCodeFragmentCountPatch = (
  count: number | null | undefined,
  qualifier?: FragmentCountQualifier | null
): FragmentCountPatch | null =>
  qualifier === MULTIPLE_FRAGMENT_QUALIFIER
    ? {
        fragmentCount: null,
        fragmentCountQualifier: MULTIPLE_FRAGMENT_QUALIFIER,
      }
    : count == null
    ? null
    : { fragmentCount: count, fragmentCountQualifier: null };

export const applyFragmentCountPatchToSpecimen = <
  State extends Record<string, SpecimenFragmentState>
>(
  state: State,
  specimenId: string,
  patch: FragmentCountPatch
): State => ({
  ...state,
  [specimenId]: {
    ...state[specimenId],
    ...patch,
  },
});
